import { MaatAuditPlanStatus } from '../constants/maat-audit-plan-status.constants';

import { LoggedInUserRoleFlags } from '../interfaces/logged-in-user-role-flags.interface';

export class MaatAuditPlanAccess {
  private loggedInUserRoleFlags: LoggedInUserRoleFlags;
  private planStatus: MaatAuditPlanStatus;

  constructor(
    loggedInUserRoleFlags: LoggedInUserRoleFlags,
    planStatus: MaatAuditPlanStatus
  ) {
    this.loggedInUserRoleFlags = loggedInUserRoleFlags;
    this.planStatus = planStatus;
  }

  private loggedInUserRoleIsMaatManager = () =>
      this.loggedInUserRoleFlags?.isMaatManager ?? false;

  private loggedInUserRoleIsMaatProjectOfficer = () =>
      this.loggedInUserRoleFlags?.isMaatProjectOfficer ?? false;

  private loggedInUserRoleIsMaatSeniorProjectOfficer = () =>
    this.loggedInUserRoleFlags?.isMaatSeniorProjectOfficer ?? false;

  private loggedInUserRoleIsMaatHeadContractor = () =>
    this.loggedInUserRoleFlags?.isMaatHeadContractor ?? false;

  canEdit(): boolean {
    const editableMaatProjectOfficerStatuses = [
      MaatAuditPlanStatus.AuditStarted,
      MaatAuditPlanStatus.DocumentsRequested,
      MaatAuditPlanStatus.DocumentsOverdue,
      MaatAuditPlanStatus.DocumentsSupplied,
      MaatAuditPlanStatus.AwaitingInspection,
      MaatAuditPlanStatus.NcqInProgress
    ];

    const editableMaatSeniorProjectOfficerStatuses = [
      MaatAuditPlanStatus.AuditStarted,
      MaatAuditPlanStatus.DocumentsRequested,
      MaatAuditPlanStatus.DocumentsOverdue,
      MaatAuditPlanStatus.DocumentsSupplied,
      MaatAuditPlanStatus.AwaitingInspection,
      MaatAuditPlanStatus.NcqInProgress,
      MaatAuditPlanStatus.ReferredToSeniorProjectOfficer
    ];

    const editableMaatManagerStatuses = [
      MaatAuditPlanStatus.ReferredToManager,
      MaatAuditPlanStatus.Completed
    ];

    const editableMaatHeadContractorStatuses = [
    ];

    const canEditForMaatProjectOfficer = () =>
      editableMaatProjectOfficerStatuses.includes(this.planStatus) &&
      this.loggedInUserRoleIsMaatProjectOfficer();

    const canEditForMaatSeniorProjectOfficer = () =>
      editableMaatSeniorProjectOfficerStatuses.includes(this.planStatus) &&
      this.loggedInUserRoleIsMaatSeniorProjectOfficer();

    const canEditForMaatManager = () =>
      editableMaatManagerStatuses.includes(this.planStatus) &&
      this.loggedInUserRoleIsMaatManager();

    const canEditForMaatHeadContractor = () =>
      editableMaatHeadContractorStatuses.includes(this.planStatus) &&
      this.loggedInUserRoleIsMaatHeadContractor();

    const canEdit = canEditForMaatProjectOfficer() ||
      canEditForMaatSeniorProjectOfficer() ||
      canEditForMaatManager() ||
      canEditForMaatHeadContractor();

    return canEdit;
  }

  canComplete(): boolean {
    return this.loggedInUserRoleIsMaatManager() && this.planStatus === MaatAuditPlanStatus.ReferredToManager;
  }

  canFinalise(): boolean {
    return this.loggedInUserRoleIsMaatManager() && this.planStatus === MaatAuditPlanStatus.Completed;
  }

  canReferToSeniorProjectOfficer(): boolean {
    return this.loggedInUserRoleIsMaatProjectOfficer() && this.planStatus === MaatAuditPlanStatus.NcqInProgress;
  }

  canReferToManager(): boolean {
    return this.loggedInUserRoleIsMaatSeniorProjectOfficer() && this.planStatus === MaatAuditPlanStatus.ReferredToSeniorProjectOfficer;
  }

  canRequestDocumentation(): boolean {
    return this.loggedInUserRoleIsMaatProjectOfficer() && this.planStatus === MaatAuditPlanStatus.AuditStarted;
  }

  canReturnToProjectOfficer(): boolean {
    return this.loggedInUserRoleIsMaatSeniorProjectOfficer() && this.planStatus === MaatAuditPlanStatus.ReferredToSeniorProjectOfficer;
  }

  canReturnToSeniorProjectOfficer(): boolean {
    return this.loggedInUserRoleIsMaatManager() && this.planStatus === MaatAuditPlanStatus.ReferredToManager;
  }

  canUndoComplete(): boolean {
    return this.loggedInUserRoleIsMaatManager() && this.planStatus === MaatAuditPlanStatus.Completed;
  }

  canExportSummaryReport(): boolean {
    const hasValidRole = this.loggedInUserRoleIsMaatProjectOfficer() ||
                         this.loggedInUserRoleIsMaatSeniorProjectOfficer() ||
                         this.loggedInUserRoleIsMaatManager();

    const exportableStatuses = [
      MaatAuditPlanStatus.Completed,
      MaatAuditPlanStatus.Finalised
    ];

    return hasValidRole && exportableStatuses.includes(this.planStatus);
  }

  canSave(): boolean {
    return !this.loggedInUserRoleIsMaatHeadContractor();
  }

  canSubmitDocumentation(): boolean {
    const submittableStatuses = [
      MaatAuditPlanStatus.DocumentsRequested,
      MaatAuditPlanStatus.DocumentsOverdue
    ];

    return this.loggedInUserRoleIsMaatHeadContractor() && submittableStatuses.includes(this.planStatus);
  }

  canSetInspectionDate(): boolean {

    const allowedStatuses = [
      MaatAuditPlanStatus.AuditStarted,
      MaatAuditPlanStatus.DocumentsRequested,
      MaatAuditPlanStatus.DocumentsOverdue,
      MaatAuditPlanStatus.DocumentsSupplied,
      MaatAuditPlanStatus.AwaitingInspection,
      MaatAuditPlanStatus.NcqInProgress
    ];

    return this.loggedInUserRoleIsMaatProjectOfficer() && allowedStatuses.includes(this.planStatus);
  }
}
